<template>
    <div class="collab-list">
        <div v-if="!items.length" class="collab-list__empty">
            {{ emptyText }}
        </div>

        <article
            v-for="item in items"
            :key="item.id"
            class="collab-card"
            @click="$emit('view-performance', item)"
        >
            <div class="collab-card__identity">
                <div
                    class="collab-card__avatar"
                    :class="item.status === 'active' ? 'is-active' : 'is-inactive'"
                    aria-hidden="true"
                >
                    {{ initials(item.name) }}
                </div>
                <div class="collab-card__who">
                    <div class="collab-card__name-row">
                        <strong>{{ item.name }}</strong>
                        <span
                            v-if="item.isResponsible"
                            class="collab-card__badge collab-card__badge--responsible"
                        >
                            {{ safeLabels.responsible || t.collaborators.responsible }}
                        </span>
                    </div>
                    <span class="collab-card__email">{{ item.email }}</span>
                </div>
            </div>

            <div class="collab-card__org">
                <span class="collab-card__org-item">
                    <fa icon="fa-diagram-project" />
                    {{ item.sectorName || '—' }}
                </span>
                <span class="collab-card__org-item">
                    <fa icon="fa-id-badge" />
                    {{ item.roleName || '—' }}
                </span>
            </div>

            <div class="collab-card__progress">
                <div class="collab-card__progress-head">
                    <span>{{ safeLabels.progress || t.collaborators.progress }}</span>
                    <strong>{{ item.progressPercent || 0 }}%</strong>
                </div>
                <div class="collab-card__progress-track" aria-hidden="true">
                    <div
                        class="collab-card__progress-fill"
                        :class="progressTone(item.progressPercent)"
                        :style="{ width: `${Math.min(100, item.progressPercent || 0)}%` }"
                    />
                </div>
            </div>

            <div class="collab-card__status">
                <span
                    class="collab-card__badge"
                    :class="item.status === 'active' ? 'is-success' : 'is-muted'"
                >
                    {{ item.status === 'active' ? activeLabel : inactiveLabel }}
                </span>
            </div>

            <div class="collab-card__actions" @click.stop>
                <v-btn
                    size="small"
                    variant="flat"
                    color="primary"
                    rounded="lg"
                    class="text-none collab-card__primary"
                    @click="$emit('view-performance', item)"
                >
                    {{ safeLabels.viewPerformance || t.collaborators.viewPerformance }}
                </v-btn>
                <v-menu location="bottom end">
                    <template #activator="{ props }">
                        <v-btn
                            v-bind="props"
                            icon
                            variant="text"
                            size="small"
                            class="collab-card__more"
                            :aria-label="t.common.moreActions"
                        >
                            <v-icon size="20">mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <v-list density="compact" min-width="200">
                        <v-list-item
                            :title="safeLabels.edit || t.collaborators.edit"
                            prepend-icon="mdi-pencil"
                            @click="$emit('edit', item)"
                        />
                        <v-list-item
                            :title="safeLabels.changeOrg || t.collaborators.changeOrg"
                            prepend-icon="mdi-sitemap"
                            @click="$emit('change-org', item)"
                        />
                    </v-list>
                </v-menu>
            </div>
        </article>
    </div>
</template>

<script>
import { corporateI18n } from '@/i18n/mixins';

export default {
    name: 'CollaboratorTable',
    mixins: [corporateI18n],
    props: {
        items: { type: Array, default: () => [] },
        labels: {
            type: Object,
            default: () => ({}),
        },
        emptyText: String,
        activeLabel: String,
        inactiveLabel: String,
    },
    emits: ['edit', 'change-org', 'view-performance'],
    computed: {
        safeLabels() {
            return this.labels || {};
        },
    },
    methods: {
        initials(name) {
            if (!name) return '?';
            const parts = String(name).trim().split(/\s+/).filter(Boolean);
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        },
        progressTone(value) {
            const n = Number(value) || 0;
            if (n >= 80) return 'is-high';
            if (n >= 40) return 'is-medium';
            return 'is-low';
        },
    },
};
</script>

<style scoped>
.collab-list {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
}

.collab-list__empty {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5c6370;
    text-align: center;
}

.collab-card {
    display: grid;
    grid-template-columns: minmax(220px, 1.4fr) minmax(160px, 1fr) minmax(140px, 0.9fr) auto auto;
    gap: 1rem;
    align-items: center;
    padding: 0.95rem 1.1rem;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    background: #fafbfc;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.collab-card:hover {
    background: #fff;
    border-color: #d5d9ef;
    box-shadow: 0 4px 14px rgba(37, 8, 145, 0.06);
}

.collab-card__identity {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;
}

.collab-card__avatar {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}

.collab-card__avatar.is-active {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.collab-card__avatar.is-inactive {
    background: #eef0f3;
    color: #5c6370;
}

.collab-card__who {
    min-width: 0;
    text-align: left;
}

.collab-card__name-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0.15rem;
}

.collab-card__name-row strong {
    color: #1a1d26;
    font-size: 0.95rem;
    line-height: 1.25;
}

.collab-card__email {
    display: block;
    font-size: 0.78rem;
    color: #5c6370;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.collab-card__org {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
    text-align: left;
}

.collab-card__org-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.78rem;
    color: #3d4350;
}

.collab-card__org-item :deep(svg) {
    width: 0.85rem;
    color: #7a8294;
}

.collab-card__progress {
    min-width: 0;
}

.collab-card__progress-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    color: #5c6370;
}

.collab-card__progress-head strong {
    color: #160266;
    font-size: 0.85rem;
}

.collab-card__progress-track {
    height: 7px;
    border-radius: 999px;
    background: #e8eaf0;
    overflow: hidden;
}

.collab-card__progress-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.25s ease;
}

.collab-card__progress-fill.is-high {
    background: #1f9d63;
}

.collab-card__progress-fill.is-medium {
    background: #250891;
}

.collab-card__progress-fill.is-low {
    background: #c47a1a;
}

.collab-card__badge {
    display: inline-flex;
    align-items: center;
    font-size: 0.68rem;
    font-weight: 700;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
}

.collab-card__badge--responsible {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.collab-card__badge.is-success {
    background: rgba(31, 157, 99, 0.12);
    color: #147a4a;
}

.collab-card__badge.is-muted {
    background: #eef0f3;
    color: #5c6370;
}

.collab-card__actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    justify-content: flex-end;
}

.collab-card__primary {
    font-weight: 600;
}

@media (max-width: 1100px) {
    .collab-card {
        grid-template-columns: 1fr 1fr;
        gap: 0.85rem;
    }

    .collab-card__identity {
        grid-column: 1 / -1;
    }

    .collab-card__actions {
        grid-column: 1 / -1;
        justify-content: flex-start;
    }
}

@media (max-width: 640px) {
    .collab-card {
        grid-template-columns: 1fr;
    }
}
</style>
